import { useCallback, useEffect, useMemo, useState } from "react";

import { AutoSizer, List as VirtualizedList } from "react-virtualized";
import { FilterMode, Metadata } from "@doitintl/cmp-models";
import { type AttributionFilter, type MetadataCloud } from "@doitintl/cmp-models";
import AddIcon from "@mui/icons-material/AddRounded";
import OpenNewIcon from "@mui/icons-material/OpenInNewRounded";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import { makeStyles } from "@mui/styles";
import difference from "lodash/difference";
import intersection from "lodash/intersection";
import sortBy from "lodash/sortBy";
import union from "lodash/union";
import without from "lodash/without";

import AmazonWebServicesIcon from "../../../../assets/aws-logo.svg";
import AmazonWebServicesIconDarkMode from "../../../../assets/aws-logo-dark-mode.svg";
import MicrosoftAzureIcon from "../../../../assets/azure-logo.svg";
import DatadogIcon from "../../../../assets/datadog-logo.png";
import DatadogIconDarkMode from "../../../../assets/datadog-logo-white.png";
import DoitLogo from "../../../../assets/doit-logo-hero-square.svg";
import GoogleCloudIcon from "../../../../assets/google-cloud.png";
import OpenaiIcon from "../../../../assets/openai-logo.svg";
import OpenaiIconDarkMode from "../../../../assets/openai-logo-white.svg";
import SnowflakeIcon from "../../../../assets/snowflake.svg";
import { alertTexts } from "../../../../assets/texts";
import { filterDialogText } from "../../../../assets/texts/CloudAnalytics";
import { globalURLs } from "../../../../assets/urls";
import { FilterModeOption } from "../../../../Pages/CloudAnalytics/allocations/types";
import {
  createFilterOptions,
  FixedFilters,
  isAttributionDimension,
  Positions,
} from "../../../../Pages/CloudAnalytics/utilities";
import { dimensionProvidersValues } from "../../../../Pages/CloudAnalytics/utils/cloudProviders";
import { type MetadataOption } from "../../../../types";
import { useFullScreen } from "../../../../utils/dialog";
import { useCloudAnalyticsTransforms } from "../../../hooks/cloudAnalytics/useCloudAnalyticsTransforms";
import { useDarkThemeCheck } from "../../../hooks/useDarkThemeCheck";
import TooltipWithMaxWidth from "../../../Tooltip/TooltipWithMaxWidth";
import { FilterInputFields } from "../components/FilterInputFields";
import { INCREASED_LIST_ITEM_HEIGHT, LIST_ITEM_HEIGHT } from "../constants";
import { type CheckboxState, type FilterProps } from "../types";
import { getInverse, getMode, getModeLabel, getPlural } from "../utils";
import useFilterStore from "./useFilterState";
import { useValidateRegEx } from "./useValidateRegEx";

const useStyles = makeStyles((theme) => ({
  listItemIcon: {
    minWidth: theme.spacing(3),
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
}));

type SimpleFilterProps = FilterProps & {
  handleCancelFilter?: (selected: MetadataOption[]) => void;
  isOptionDisabled?: (option: string) => boolean;
  cloudProviders?: MetadataCloud[] | null;
  inverse: boolean;
  filter?: AttributionFilter | null | undefined;
  numOfListItems: number;
  selected: MetadataOption;
  onSaveFilters: (filtersOrRegex: string[] | string) => void;
};

const useFilterDialog = ({
  selected,
  filter,
  onSaveFilters,
  handleCancelFilter,
  isOptionDisabled,
  cloudProviders,
  inverse,
  numOfListItems,
}: SimpleFilterProps) => {
  const { fullScreen } = useFullScreen();
  const filterAlerts = useMemo(
    () => ({
      [`${Metadata.FIXED}:project_ancestry_names`]: (md) => {
        const t = (md?.data?.values?.slice(1) ?? []).findIndex((v) => /[a-zA-Z]/.test(v));
        if (t === -1) {
          return {
            severity: "info",
            action: (
              <Button
                color="inherit"
                size="small"
                component={Link}
                href="https://help.doit.com/google-cloud/connect-google-cloud-service-account"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Learn more"
                startIcon={<OpenNewIcon />}
                style={{ whiteSpace: "nowrap" }}
              >
                {alertTexts.LEARN_MORE}
              </Button>
            ),
            message: <>Connect your Google Cloud organization to show folder names instead of folder numbers</>,
          };
        }
      },
    }),
    []
  );
  const transforms = useCloudAnalyticsTransforms();
  const isDirrect = selected.mode === FilterMode.IS;
  const { validateInputForRE2 } = useValidateRegEx();
  const isFixed = selected.data.type === "fixed";
  const classes = useStyles();
  const {
    mode,
    inputValue,
    setInputValue,
    setMode,
    filtersStateChanged,
    setFiltersStateChanged,
    filters,
    selectedValues,
    setFilters,
  } = useFilterStore();
  const alert = filterAlerts?.[selected.id]?.(selected) ?? null;
  const [options, setOptions] = useState<string[]>([]);
  const isRegexMode = useMemo(
    () => mode === FilterModeOption.MATCHES_REGEX || mode === FilterModeOption.DOES_NOT_MATCHES_REGEX,
    [mode]
  );
  const isContainsMode =
    mode === FilterModeOption.CONTAINS ||
    mode === FilterModeOption.DOES_NOT_CONTAIN ||
    mode === FilterModeOption.STARTS_WITH ||
    mode === FilterModeOption.ENDS_WITH;

  const [checkbox, setCheckbox] = useState<CheckboxState>({
    primary: "",
    indeterminate: false,
    checked: false,
  });
  const isAttribution = isAttributionDimension(selected);

  const isDarkMode = useDarkThemeCheck();

  useEffect(() => {
    setOptions([]);
    setFilters(isDirrect ? (selected._filter ?? []) : []);
    setMode(getModeLabel(filter?.mode || selected?.mode, inverse) ?? FilterModeOption.IS);
    setCheckbox({
      primary: "",
      indeterminate: false,
      checked: false,
    });
  }, [filter?.mode, inverse, isDirrect, selected._filter, selected?.mode, setFilters, setFiltersStateChanged, setMode]);

  const { label, plural } = getPlural(selected);

  const transform = useMemo(() => {
    if (!transforms) {
      return null;
    }

    if (selected.data.type === Metadata.ATTRIBUTION_GROUP) {
      return transforms[Metadata.ATTRIBUTION_GROUP] ?? null;
    }

    return transforms[selected.id] ?? null;
  }, [transforms, selected]);

  const filteredOptions = useMemo(() => {
    const filterOptions = createFilterOptions<string>({
      trim: true,
      ignoreAccents: true,
      ignoreCase: true,
      matchFrom: "any",
      stringify: (option: string) => transform?.(option, selected.data.nullFallback) ?? option,
    });
    if (isContainsMode) {
      return options;
    }
    return inputValue.split(",").reduce(
      (acc: string[], item) => [
        ...acc,
        ...filterOptions(options, {
          inputValue: item,
          getOptionLabel: (option) => option,
        }),
      ],
      []
    );
  }, [isContainsMode, inputValue, transform, selected.data.nullFallback, options]);

  const canAddCustomValue =
    selected.data.key !== FixedFilters.CLOUD &&
    selected.data.key !== Metadata.ATTRIBUTION &&
    selected.data.key !== Metadata.ATTRIBUTION_GROUP;
  const numSelectedOptions = filters.length;
  const numOptions = options.length;
  const numFilteredOptions = filteredOptions.length;

  useEffect(() => {
    if (options.length) {
      return;
    }

    let newOptions: string[] = [];
    const isUserCreatedDimensions =
      selected.data.type === Metadata.ATTRIBUTION || selected.data.type === Metadata.ATTRIBUTION_GROUP;

    if (!cloudProviders?.length || isUserCreatedDimensions) {
      newOptions = selected.data.values ?? [];
    } else {
      newOptions = dimensionProvidersValues(selected, cloudProviders);
    }

    if (isUserCreatedDimensions) {
      newOptions = union(filters, newOptions);
    } else {
      newOptions = union(sortBy(filters), sortBy(newOptions));
    }

    if (selected.data.nullFallback) {
      newOptions = union([selected.data.nullFallback], newOptions);
    }

    setOptions(newOptions);
  }, [selected, cloudProviders, filters, options.length]);

  useEffect(() => {
    if (isContainsMode) {
      setCheckbox((checkbox) => ({
        ...checkbox,
        primary: filterDialogText.ALL_OPTIONS(plural, numOptions),
      }));
      return;
    }
    if (inputValue === "") {
      if (numSelectedOptions === 0) {
        setCheckbox((checkbox) => ({ ...checkbox, primary: filterDialogText.ALL_OPTIONS(plural, numOptions) }));
      } else if (numSelectedOptions === numOptions) {
        setCheckbox((checkbox) => ({
          ...checkbox,
          primary: filterDialogText.ALL_OPTIONS_SELECTED(numOptions > 1 ? numOptions : ""),
        }));
      } else {
        setCheckbox((checkbox) => ({
          ...checkbox,
          primary: filterDialogText.SELECTED_OUT_OF(numSelectedOptions, numOptions),
        }));
      }
    } else {
      setCheckbox((checkbox) => ({ ...checkbox, primary: filterDialogText.FILTERED_RESULTS(numFilteredOptions) }));
    }
  }, [inputValue, numOptions, numSelectedOptions, numFilteredOptions, plural, isContainsMode]);

  const addCustomOption = useCallback(() => {
    setOptions((options) => {
      const newOptions = options.slice();
      newOptions.splice(selected.data.nullFallback ? 1 : 0, 0, inputValue);
      return newOptions;
    });
    setFilters([...filters, inputValue]);
    setInputValue("");
  }, [filters, inputValue, selected.data.nullFallback, setFilters, setInputValue]);

  const showNoMatchesLine = useMemo(() => {
    const noExactMatch = inputValue.length > 0 && !filteredOptions.includes(inputValue);
    return (canAddCustomValue && noExactMatch) || (!canAddCustomValue && numFilteredOptions === 0);
  }, [inputValue, filteredOptions, canAddCustomValue, numFilteredOptions]);
  let regularVirtualListHeight = LIST_ITEM_HEIGHT * numOfListItems;

  if (isContainsMode) {
    regularVirtualListHeight = LIST_ITEM_HEIGHT * (numOfListItems + 1);
  }

  const saveFilterChanges = useCallback(async () => {
    if (isRegexMode) {
      const validRE2 = await validateInputForRE2(inputValue);
      if (validRE2) {
        onSaveFilters(inputValue);
      }
      return;
    }

    if (isContainsMode) {
      onSaveFilters(inputValue.split(",").map((v) => v.trim()));
      return;
    }

    if (filters.length === 0 && handleCancelFilter && selected._position === Positions.UNUSED) {
      handleCancelFilter([selected]);
    } else {
      onSaveFilters(filters);
    }
  }, [
    isRegexMode,
    isContainsMode,
    filters,
    handleCancelFilter,
    selected,
    validateInputForRE2,
    inputValue,
    onSaveFilters,
  ]);

  const disabled = useMemo(() => {
    // Regex mode: disabled if no input or no changes
    if (isRegexMode) {
      return !inputValue.length || (!filtersStateChanged && inputValue === selected._regexp);
    }

    // Contains mode: disabled if no changes, no values, or values unchanged
    if (isContainsMode) {
      const hasNoValues = !selectedValues.length;
      const valuesUnchanged = selectedValues.join(", ") === selected._filter?.join(", ");
      return (
        !filtersStateChanged ||
        hasNoValues ||
        (valuesUnchanged && getMode(mode) === selected?.mode && getInverse(mode) === selected?._inverse)
      );
    }
    // Standard mode: disabled if no changes and (unused position or attribution)
    const isUnusedOrAttribution = selected._position === Positions.UNUSED || isAttribution;
    return !filters.length || (!filtersStateChanged && isUnusedOrAttribution);
  }, [
    filters.length,
    filtersStateChanged,
    inputValue,
    isAttribution,
    isContainsMode,
    isRegexMode,
    mode,
    selected._filter,
    selected?._inverse,
    selected._position,
    selected._regexp,
    selected?.mode,
    selectedValues,
  ]);

  useEffect(() => {
    if (selected._position === Positions.UNUSED || isAttribution) {
      const diff1 = difference(selected._filter ?? [], filters);
      const diff2 = difference(filters, selected._filter ?? []);
      if (diff1.length > 0 || diff2.length > 0) {
        setFiltersStateChanged(true);
      } else {
        setFiltersStateChanged(false);
      }
    }
  }, [
    filters,
    inverse,
    isAttribution,
    selected._filter,
    selected._inverse,
    selected._position,
    setFiltersStateChanged,
  ]);

  useEffect(() => {
    if (isContainsMode) {
      setCheckbox((checkbox) => ({
        ...checkbox,
        indeterminate: false,
        checked: false,
      }));
      return;
    }
    const numFilteredSelectedOptions = intersection(filteredOptions, filters).length ?? 0;
    const allSelected = numFilteredSelectedOptions === numFilteredOptions;
    setCheckbox((checkbox) => ({
      ...checkbox,
      indeterminate: !allSelected && numFilteredSelectedOptions > 0,
      checked: numFilteredSelectedOptions !== 0,
    }));
  }, [filteredOptions, filters, isContainsMode, numFilteredOptions]);

  const renderRow = useCallback(
    (props) => {
      const { key, index, style } = props;
      const option = filteredOptions[index];
      const checked = !isContainsMode ? filters.includes(option) : false;
      const isDisabled = isOptionDisabled?.(option) ?? false;

      const providerValues = {
        aws: (selected._values.awsValues ?? []).includes(option),
        gcp: (selected._values.gcpValues ?? []).includes(option),
        azure: (selected._values.azureValues ?? []).includes(option),
        datahub: (selected._values.datahub ?? []).includes(option),
        snowflake: (selected._values.snowflake ?? []).includes(option),
        datadog: (selected._values.datadog ?? []).includes(option),
        openai: (selected._values.openai ?? []).includes(option),
      };

      const toggleOption = () => {
        if (isContainsMode) {
          return;
        }
        setFilters(checked ? without(filters, option) : union(filters, [option]));
      };

      const providerIcons = [
        { show: providerValues.aws, src: isDarkMode ? AmazonWebServicesIconDarkMode : AmazonWebServicesIcon },
        { show: providerValues.gcp, src: GoogleCloudIcon },
        { show: providerValues.azure, src: MicrosoftAzureIcon },
        { show: providerValues.datahub, src: DoitLogo },
        { show: providerValues.snowflake, src: SnowflakeIcon },
        { show: providerValues.datadog, src: isDarkMode ? DatadogIconDarkMode : DatadogIcon },
        { show: providerValues.openai, src: isDarkMode ? OpenaiIconDarkMode : OpenaiIcon },
      ];

      const displayText = transform?.(option, selected.data.nullFallback) ?? option;
      const showTooltip = option?.length > 60;

      return (
        <Stack
          spacing={1}
          direction="row"
          key={key}
          style={style}
          onClick={toggleOption}
          data-testid={index}
          sx={{
            opacity: isDisabled || isContainsMode ? 0.5 : 1,
            alignItems: "center",
            cursor: isDisabled || isContainsMode ? "not-allowed" : "pointer",
          }}
        >
          <Checkbox
            id={option}
            checked={checked}
            disableRipple
            disabled={isDisabled || isContainsMode}
            inputProps={{ "aria-labelledby": option }}
            edge="start"
            size="small"
            color="primary"
            data-testid={option}
            sx={{ p: 0 }}
          />
          <Box
            sx={{
              flexGrow: 1,
              maxWidth: "calc(100% - 75px)",
              textOverflow: "ellipsis",
            }}
          >
            <TooltipWithMaxWidth title={showTooltip ? option : ""}>
              <Typography noWrap variant="body2">
                {displayText}
              </Typography>
            </TooltipWithMaxWidth>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            {providerIcons.map(
              (icon, i) =>
                icon.show && (
                  <Box key={i} sx={{ width: "20px" }}>
                    <Box component="img" src={icon.src} sx={{ height: "20px", width: "20px" }} aria-hidden />
                  </Box>
                )
            )}
          </Box>
        </Stack>
      );
    },
    [
      filteredOptions,
      isContainsMode,
      filters,
      isOptionDisabled,
      selected._values.awsValues,
      selected._values.gcpValues,
      selected._values.azureValues,
      selected._values.datahub,
      selected._values.snowflake,
      selected._values.datadog,
      selected._values.openai,
      selected.data.nullFallback,
      isDarkMode,
      transform,
      setFilters,
    ]
  );

  const listItemTextPrimary =
    numFilteredOptions > 0 ? filterDialogText.NO_EXACT_MATCH(inputValue) : filterDialogText.NO_MATCHES(inputValue);

  const listItemTextSecondary = canAddCustomValue ? filterDialogText.CLICK_TO_ADD : undefined;
  const dialog = (
    <Box data-testid="RegularFilterContent" sx={{ display: "flex", flexDirection: "column" }}>
      {alert && (
        <Alert
          variant="standard"
          severity={alert?.severity ?? "info"}
          action={alert?.action ?? undefined}
          className={classes.alert}
        >
          {alert?.message}
        </Alert>
      )}
      <FilterInputFields label={label} isFixed={isFixed} selected={selected} />
      {isRegexMode ? (
        <>
          <Alert variant="standard" severity="warning" sx={{ whiteSpace: "pre-line", mb: 2 }}>
            {filterDialogText.REGEX_ALERT_PERFORMANCE}
          </Alert>
          <Alert variant="standard" severity="info" sx={{ whiteSpace: "pre-line" }}>
            {filterDialogText.REGEX_ALERT_PART_1}
            <Link
              target="_blank"
              rel="noopener noreferrer"
              href={globalURLs.REGEXP_HELP}
              color="inherit"
              underline="always"
            >
              {filterDialogText.RE2}
            </Link>
            {filterDialogText.REGEX_ALERT_PART_2}
            <strong>{filterDialogText.REGEX_ALERT_EXAMPLE}</strong> {filterDialogText.REGEX_ALERT_EXAMPLE_TEXT}
          </Alert>
        </>
      ) : (
        <>
          {showNoMatchesLine && !isContainsMode && (
            <List disablePadding>
              <ListItem
                divider
                sx={{ height: INCREASED_LIST_ITEM_HEIGHT }}
                secondaryAction={
                  canAddCustomValue && (
                    <IconButton edge="end" aria-label="add" size="small" onClick={addCustomOption}>
                      <AddIcon />
                    </IconButton>
                  )
                }
              >
                <ListItemText
                  primary={listItemTextPrimary}
                  secondary={listItemTextSecondary}
                  primaryTypographyProps={{ variant: "subtitle2" }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItem>
            </List>
          )}
          {!!options.length && (
            <>
              {!!numFilteredOptions && (
                <ListItemButton
                  divider
                  disabled={isContainsMode}
                  onClick={() => {
                    if (checkbox.indeterminate) {
                      setFilters(union(filters, filteredOptions));
                    } else if (checkbox.checked) {
                      setFilters(difference(filters, filteredOptions));
                    } else {
                      setFilters(union(filters, filteredOptions));
                    }
                  }}
                  sx={{ height: INCREASED_LIST_ITEM_HEIGHT, flexGrow: 0, pl: 0.35 }}
                >
                  <ListItemIcon className={classes.listItemIcon}>
                    <Checkbox
                      checked={checkbox.checked}
                      indeterminate={checkbox.indeterminate}
                      tabIndex={-1}
                      disableRipple
                      disabled={isContainsMode}
                      inputProps={{ "aria-labelledby": "main-checkbox" }}
                      edge="start"
                      size="small"
                      color="primary"
                    />
                  </ListItemIcon>
                  <ListItemText
                    id="main-checkbox"
                    primary={checkbox.primary}
                    primaryTypographyProps={{ variant: "subtitle2" }}
                  />
                </ListItemButton>
              )}

              <Box data-testid="list-items" sx={{ flexGrow: 1 }}>
                <AutoSizer disableHeight={!fullScreen}>
                  {({ width, height }) => (
                    <VirtualizedList
                      rowHeight={LIST_ITEM_HEIGHT}
                      height={fullScreen ? height : regularVirtualListHeight}
                      rowCount={numFilteredOptions}
                      rowRenderer={renderRow}
                      width={width}
                    />
                  )}
                </AutoSizer>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );

  return { dialog, disabled, regexDisabled: false, saveFilterChanges };
};

export default useFilterDialog;
